// combatpro/views/WeaponsView.js
//
// Stage 33: view-weapons shell. Stage 69 (Weapons View Foundation) migrates the
// legacy per-monster Weapons & Attacks card (battletracker.html weapons-block,
// 6039-6087, renderer refreshWeapons 8261, add/remove flows 8190-8259) for the
// ACTIVE monster.
//
// The legacy built one weapons-block-${i} per monster inside createMonsterPanel and
// reparented the active one into #vi-weapons-mount via vi_mountWeapons (10021). React
// renders the active monster's card directly from state.currentCombatantIndex (the
// MoraleView / AbilityScoresView / AbilitiesView pattern) - no reparenting, no
// getElementById.
//
// Faithful migration (master spec preserved verbatim):
//   - the COLLAPSIBLE section header (⚔️ Weapons & Attacks). The legacy toggleSection
//     (9290) toggles a "collapsed" class on the section; the CSS
//     (.section-collapsible.collapsed .section-content { max-height:0 }) drives the
//     collapse and rotates the ::after chevron. Mirrored here with view-local collapse
//     state that adds / removes the "collapsed" class on the section className - no DOM
//     manipulation. Default is expanded (no collapsed class), matching the markup.
//   - the two static guide cards (How Combat Works / How to Use, 6045-6064) and the
//     three static attack-button guide cards (6067-6081).
//   - the weapons list (refreshWeapons 8261): empty -> "No weapons"; else a
//     vi-weapon-grid of vi-weapon-card entries showing the STORED name, +bonus,
//     damage, type, range and the four action buttons plus a ✕ remove button.
//   - the "+ Add Weapon" picker (addWeapon 8190 / confirmAddWeapon 8215): a select of
//     [official DB + view-local custom weapons] (filtered to entries with a name) by
//     index plus a "+ Add Custom Weapon" sentinel; Add resolves the choice and commits,
//     Cancel closes. Custom-add collects name (required), bonus (parseInt||0), damage,
//     type, range (default "5 ft.") via five window.prompts.
//
// DEFERRED INTEGRATION POINTS (Stage 69 scope = page structure + ownership only):
//   The per-weapon action buttons - 🎯 Attack, ADV, DIS, 💥 Damage - belong to the
//   combat ROLLING subsystem (legacy rollAttack 6828 / rollWeaponDamage 6887, which
//   drive the dice roller, Last Actions log, and the resistance/apply-damage modal).
//   That subsystem is NOT yet migrated. Per the approved Stage 69 plan (Option A) the
//   four buttons are rendered EXACTLY as in the legacy (labels / order / classes /
//   placement preserved) but their handlers are explicit no-op placeholder seams wired
//   in a later stage. No partial rolling behaviour is invented and the unmigrated
//   subsystem is not duplicated. The ✕ Remove button is fully implemented this stage.
//
// Ownership: monster state is owned by CombatContext. Entry add/remove go through the
// Stage 69 addMonsterWeapon / removeMonsterWeapon handlers (the nested weapons writes
// updateMonsterField cannot express; no dedup - duplicates permitted, matching legacy).
// The official weapon database is reused from window.VI_WEAPONS_DB (the same window.VI_*
// convention the Stage 61 selection runtime uses for abilities) - not re-bundled; if it
// is unavailable at runtime the picker gracefully degrades to custom entries plus the
// custom sentinel. View-local state owns only UI concerns: collapse state, the picker
// visibility + selected value, and the session custom-weapon accumulator. Custom-weapon
// entry keeps the legacy window.prompt interaction; removal keeps window.confirm. No DOM
// manipulation, no getElementById, no createElement, no innerHTML, no new runtime, no
// CombatContext ownership change. No optional chaining. No em dash.

import React, { useContext, useState, useEffect } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { DAMAGE_TYPES } from '../damage/damageTypes';
import { computeFinalDamage } from '../damage/damageEngine';

// Read the official weapon database via the same window.VI_* convention the Stage 61
// runtime uses for abilities. Graceful degradation: missing / non-array -> [].
function readWeaponsDb() {
  return (typeof window !== 'undefined' && Array.isArray(window.VI_WEAPONS_DB)) ? window.VI_WEAPONS_DB : [];
}

function WeaponsView() {
  const { state, addMonsterWeapon, removeMonsterWeapon, rollAttack, rollWeaponDamage, applyDamage } = useContext(CombatContext);
  const index = state.currentCombatantIndex;
  const monster = state.monsters[index];

  // View-local UI state (not shared application state).
  const [collapsed, setCollapsed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState('');
  const [customWeapons, setCustomWeapons] = useState([]);

  // Stage 92 (Apply Damage Modal Foundation): view-local state for the Apply
  // Damage modal, mirroring the weapon-picker modal's local-state pattern. This is
  // UI state only - no shared application state, no HP, no runtime ownership.
  //   - applyDamageOpen: modal visibility (default closed). The bridge that opens
  //     it after rollWeaponDamage (legacy showApplyDamageModal call 6904) is
  //     DEFERRED to Stage 95; nothing opens it this stage.
  //   - applyDamageContext: the {damage, attackerName, weaponName} the legacy modal
  //     was built from (showApplyDamageModal args 6907). Held so the future bridge
  //     can populate it; defaults are inert.
  //   - applyTarget: the selected target index (legacy damageTarget select). Default
  //     '0' (legacy first option), matching the weapon picker's '0' default.
  //   - applyDamageType: the selected damage type (legacy damageType select).
  //     Default DAMAGE_TYPES[0], exactly as ResistanceView's calculator default.
  //   - applyFinalDamage: the editable Final Damage field (legacy adjustedDamage
  //     input 6958), initialised to the raw rolled damage. Stage 93 will wire
  //     computeFinalDamage to auto-adjust it; this stage leaves it as the raw value.
  const [applyDamageOpen, setApplyDamageOpen] = useState(false);
  const [applyDamageContext, setApplyDamageContext] = useState({ damage: 0, attackerName: '', weaponName: '' });
  const [applyTarget, setApplyTarget] = useState('0');
  const [applyDamageType, setApplyDamageType] = useState(DAMAGE_TYPES[0]);
  const [applyFinalDamage, setApplyFinalDamage] = useState('0');

  // Hoisted above the early return so hooks run in a stable order every render
  // (rules-of-hooks). These derivations do not reference the active monster.
  const applyTargetIndex = parseInt(applyTarget, 10);
  const applyTargetMonster = (!isNaN(applyTargetIndex) && state.monsters)
    ? state.monsters[applyTargetIndex]
    : null;
  const applyRawDamage = applyDamageContext.damage;

  useEffect(function () {
    if (!applyDamageOpen) { return; }
    if (!applyTargetMonster) { return; }
    const original = parseInt(applyRawDamage, 10);
    if (isNaN(original)) { return; }
    const finalDamage = computeFinalDamage(
      original,
      applyDamageType,
      applyTargetMonster.resistances || '',
      applyTargetMonster.immunities || '',
      applyTargetMonster.vulnerabilities || ''
    );
    setApplyFinalDamage('' + finalDamage);
  }, [applyDamageOpen, applyTarget, applyDamageType, applyRawDamage]);

  if (!monster) {
    return (
      <div className="vi-view" id="view-weapons">
        <div id="vi-weapons-mount"></div>
      </div>
    );
  }

  const weapons = Array.isArray(monster.weapons) ? monster.weapons : [];
  const db = readWeaponsDb();

  // Picker pool: [official DB + custom] filtered to entries with a name, addressed by
  // index (addWeapon 8191-8193 / confirmAddWeapon 8241-8242).
  const allForPicker = db.concat(customWeapons).filter(function(w) { return w && w.name; });

  function openPicker() {
    setPickerValue(allForPicker.length ? '0' : '__CUSTOM__');
    setPickerOpen(true);
  }

  function closePicker() {
    setPickerOpen(false);
  }

  // Stage 92: close the Apply Damage modal (legacy Cancel button 6963: the modal
  // simply removed itself). No state mutation, no HP change, no logging - just
  // visibility off, faithful to the legacy Cancel.
  function closeApplyDamage() {
    setApplyDamageOpen(false);
  }

  // Stage 93 (Apply Damage Resistance Integration): wire the existing Stage 76
  // resistance engine (damageEngine.computeFinalDamage) into the modal, faithful to
  // the legacy calculateResistance / updateResistanceInfo (battletracker.html
  // 6975-7041). No resistance math is reimplemented here - the engine owns it; this
  // only RECOMPUTES Final Damage on target / damage-type change and DERIVES the
  // status display, exactly mirroring ResistanceView's number-comparison pattern.
  //
  // The selected target's resist / immune / vuln strings drive the engine. The raw
  // rolled damage (applyDamageContext.damage) is the engine input, matching the
  // legacy damage-type onchange which passed the original damage; recomputing from
  // raw (not the editable field) preserves the legacy behaviour where a target /
  // type change overwrites the field (adjustedDamage.value = finalDamage, 7020)
  // while manual edits between changes are left intact (the field stays editable).

  // Stage 94 (Apply Damage Runtime): the Apply button handler. Faithful port of the
  // legacy applyDamageToTarget (battletracker.html 7110-7147), composing the
  // existing CombatContext applyDamage runtime - HP subtraction (floor at 0), death
  // detection, and morale are ALL owned by applyDamage and are NOT reimplemented
  // here. Execution order mirrors legacy:
  //   1. read targetIndex (legacy 7111) and the already-adjusted Final Damage from
  //      the field (legacy adjustedDamage 7112). Manual edits are respected: the
  //      current applyFinalDamage value is used as-is, NOT recomputed (Stage 93
  //      already did resistance).
  //   2. NaN guard (legacy 7114-7117): if either is NaN, just close the modal and
  //      return - no application.
  //   3. applyDamage(targetIndex, finalDamage) - the existing runtime does the HP
  //      write (max(0, oldHP - dmg)), death detection, and morale checks (legacy
  //      7120-7137), plus its established Stage 80 'damage' Last Actions feeder.
  //   4. close the modal (legacy 7146).
  //
  // DEFERRED: the legacy addCombatLog (7143) is Combat Log integration - Stage 96,
  // not added here. No sidebar guidance, no visual effects.
  function applyDamageFromModal() {
    const targetIndex = parseInt(applyTarget, 10);
    const finalDamage = parseInt(applyFinalDamage, 10);
    if (isNaN(targetIndex) || isNaN(finalDamage)) {
      closeApplyDamage();
      return;
    }
    applyDamage(targetIndex, finalDamage);
    closeApplyDamage();
  }

  // Recompute Final Damage when target or damage type (or the incoming raw damage)
  // changes. Keyed on those only - NOT on applyFinalDamage - so manual edits are not
  // clobbered until the next target / type change, faithful to legacy.

  // Derive the resistance information display from the engine number, mirroring the
  // legacy modal calculateResistance (6975-7035): the status is re-derived by
  // comparing finalDamage to the original (immune = 0, resistant < original,
  // vulnerable > original, normal = equal). Uses the MODAL's exact wording / colours
  // (6996 / 7004 / 7012), and the modal's rule that NORMAL hides the area
  // (display:none, 7031-7033) - returns null so nothing renders. Engine math is not
  // duplicated; only the label / colour are derived, like ResistanceView.
  function deriveApplyResistanceDisplay() {
    if (!applyTargetMonster) { return null; }
    const original = parseInt(applyRawDamage, 10);
    if (isNaN(original)) { return null; }
    const finalDamage = computeFinalDamage(
      original,
      applyDamageType,
      applyTargetMonster.resistances || '',
      applyTargetMonster.immunities || '',
      applyTargetMonster.vulnerabilities || ''
    );
    if (finalDamage === 0) {
      return {
        text: '\ud83d\udee1\ufe0f IMMUNE to ' + applyDamageType + ' damage! ' + original + ' \u2192 0',
        color: '#3498db',
        background: 'rgba(52, 152, 219, 0.1)'
      };
    }
    if (finalDamage < original) {
      return {
        text: '\ud83d\udd30 RESISTANT to ' + applyDamageType + ' damage! ' + original + ' \u2192 ' + finalDamage + ' (halved)',
        color: '#f39c12',
        background: 'rgba(243, 156, 18, 0.1)'
      };
    }
    if (finalDamage > original) {
      return {
        text: '\u26a0\ufe0f VULNERABLE to ' + applyDamageType + ' damage! ' + original + ' \u2192 ' + finalDamage + ' (doubled)',
        color: '#e74c3c',
        background: 'rgba(231, 76, 60, 0.1)'
      };
    }
    // Normal: legacy hides the area (display:none) - render nothing.
    return null;
  }
  const applyResistanceDisplay = applyDamageOpen ? deriveApplyResistanceDisplay() : null;

  // confirmAddWeapon (8215): custom path uses five prompts; otherwise resolve the chosen
  // pool entry by index and commit through the handler (the handler clones).
  function confirmAdd() {
    var value = pickerValue;
    if (value === '__CUSTOM__') {
      var name = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Weapon name:') : null;
      if (!name || !name.trim()) { closePicker(); return; }
      var bonus = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Attack bonus (e.g., +5):') : null;
      var damage = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Damage (e.g., 2d6+3):') : null;
      var type = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Damage type (e.g., Slashing):') : null;
      var range = (typeof window !== 'undefined' && window.prompt) ? window.prompt('Range (e.g., 5 ft.):') : null;
      var newWeapon = {
        name: name.trim(),
        bonus: parseInt(bonus, 10) || 0,
        damage: damage || '',
        type: type || '',
        range: range || '5 ft.'
      };
      setCustomWeapons(function(prev) { return prev.concat([newWeapon]); });
      addMonsterWeapon(index, newWeapon);
    } else {
      var selected = allForPicker[parseInt(value, 10)];
      if (selected) { addMonsterWeapon(index, selected); }
    }
    closePicker();
  }

  function onRemove(weaponIndex) {
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Remove this weapon?')) { return; }
    removeMonsterWeapon(index, weaponIndex);
  }

  // Mirrors toggleSection (9290): toggle the "collapsed" class on the section.
  function toggleCollapse() {
    setCollapsed(function(prev) { return !prev; });
  }

  // Stage 85 (Weapon Attack Runtime): the Attack / ADV / DIS buttons call the
  // CombatContext rollAttack runtime directly with the active monster index and
  // the per-weapon index (passed inline at the button, where both are in scope).
  // Stage 86 (Weapon Damage Runtime): the Damage button likewise calls the
  // CombatContext rollWeaponDamage runtime with (index, weaponIndex, false) - a
  // normal (non-crit) damage roll, matching the legacy Damage control. The former
  // deferredDamage no-op stub is removed. The apply-damage modal, HP modification,
  // target selection, and combat log remain deferred (owned by a later stage).

  var sectionClassName = 'section-collapsible weapons-section' + (collapsed ? ' collapsed' : '');

  return (
    <div className="vi-view" id="view-weapons">
      <div id="vi-weapons-mount">
        <div id={'weapons-block-' + index}>
          <div className={sectionClassName} id={'weapons-section-' + index}>
            <div className="section-header vi-section-header" onClick={toggleCollapse}>
              <h3>⚔️ Weapons & Attacks</h3>
            </div>
            <div className="section-content vi-section-body">
              <div className="vi-ability-helper-grid vi-weapons-cards">
                <div className="vi-ability-card">
                  <div className="vi-ability-title">How Combat Works</div>
                  <div className="vi-howto-steps">
                    <p>Attack rolls determine if a hit succeeds.</p>
                    <p>Advantage / Disadvantage adjusts the roll outcome.</p>
                    <p>Successful hits allow damage to be rolled.</p>
                    <p>Damage is processed through resistance calculations before applying.</p>
                  </div>
                </div>
                <div className="vi-ability-card">
                  <div className="vi-ability-title">How to Use</div>
                  <div className="vi-howto-steps">
                    <p>Click Attack to roll and determine hit success.</p>
                    <p>If successful, click Damage to roll damage.</p>
                    <p>Review resistance in the modal.</p>
                    <p>Confirm to apply final damage.</p>
                  </div>
                  <p>Combat flow: Attack {'\u2192'} Hit {'\u2192'} Damage {'\u2192'} Confirm.</p>
                </div>
              </div>

              <div className="vi-weapon-guide-cards">
                <div className="vi-weapon-guide-card">
                  <div className="vi-weapon-guide-title">🎯 Attack / ADV / DIS</div>
                  <div className="vi-weapon-guide-body">Click to roll d20 + bonus {'\u2192'} result logged to Last Actions</div>
                </div>
                <div className="vi-weapon-guide-card">
                  <div className="vi-weapon-guide-title">💥 Damage</div>
                  <div className="vi-weapon-guide-body">Click to roll damage dice {'\u2192'} opens resistance modal {'\u2192'} applies final damage</div>
                </div>
                <div className="vi-weapon-guide-card">
                  <div className="vi-weapon-guide-title">⚔️ Combat Flow</div>
                  <div className="vi-weapon-guide-body">Attack {'\u2192'} if hit {'\u2192'} Damage {'\u2192'} modal calculates resistance {'\u2192'} confirm to apply</div>
                </div>
              </div>

              <div id={'weapons-list-' + index} className="vi-mb-10">
                {weapons.length === 0 ? (
                  <p className="vi-modal-result-text">No weapons</p>
                ) : (
                  <div className="vi-weapon-grid">
                    {weapons.map(function(weapon, i) {
                      return (
                        <div className="vi-weapon-card" key={i}>
                          <div className="vi-weapon-inner">
                            <div className="vi-weapon-info">
                              <div className="vi-weapon-name">{weapon.name}</div>
                              <div className="vi-text-subtle">
                                <div className="vi-weapon-tags">
                                  <span className="vi-weapon-tag">{'+' + weapon.bonus}</span>
                                  <span className="vi-weapon-tag">{weapon.damage}</span>
                                  <span className="vi-weapon-tag">{weapon.type}</span>
                                </div>
                              </div>
                              <div className="vi-weapon-range">{weapon.range}</div>
                              <div className="vi-weapon-btns">
                                <button type="button" onClick={function() { rollAttack(index, i, false, false); }} className="btn-info vi-btn vi-btn-info vi-btn-xs">🎯 Attack</button>
                                <button type="button" onClick={function() { rollAttack(index, i, true, false); }} className="btn-success vi-btn vi-btn-xs">ADV</button>
                                <button type="button" onClick={function() { rollAttack(index, i, false, true); }} className="btn-danger vi-btn vi-btn-xs">DIS</button>
                                <button type="button" onClick={function() {
                                  // Stage 95 (Weapon Damage -> Apply Damage bridge): roll the weapon
                                  // damage via the unchanged CombatContext runtime, then transfer the
                                  // returned legacy payload (total / monsterName / weaponName, exactly
                                  // what showApplyDamageModal received at 6904) into the modal state and
                                  // open the completed Apply Damage modal. The legacy showDamageRollOption
                                  // confirm() (6875-6884) is intentionally NOT recreated - the modal
                                  // replaces it per spec. No gameplay logic here: WeaponsView owns only
                                  // this data transfer + modal open.
                                  var result = rollWeaponDamage(index, i, false);
                                  if (!result) { return; }
                                  setApplyDamageContext({ damage: result.total, attackerName: result.monsterName, weaponName: result.weaponName });
                                  setApplyFinalDamage('' + result.total);
                                  setApplyTarget('0');
                                  setApplyDamageOpen(true);
                                }} className="btn-warning vi-btn vi-btn-info vi-btn-xs">💥 Damage</button>
                              </div>
                            </div>
                            <button type="button" onClick={(function(wi) { return function() { onRemove(wi); }; })(i)} className="vi-btn vi-btn-xs">✕</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button type="button" onClick={openPicker} className="btn-warning vi-btn vi-btn-secondary">+ Add Weapon</button>
            </div>
          </div>
        </div>
      </div>

      {pickerOpen ? (
        <div className="weapon-modal-overlay">
          <div className="vi-modal-overlay">
            <div className="vi-modal-box-sm">
              <h3 className="vi-modal-title">Select Weapon</h3>
              <select id="weaponDropdown" className="vi-modal-select" value={pickerValue} onChange={function(e) { setPickerValue(e.target.value); }}>
                {allForPicker.map(function(w, i) {
                  return <option key={i} value={'' + i}>{w.name + ' (' + w.damage + ' ' + w.type + ', ' + w.range + ')'}</option>;
                })}
                <option value="__CUSTOM__">+ Add Custom Weapon</option>
              </select>
              <div className="vi-modal-actions">
                <button type="button" onClick={closePicker} className="vi-btn vi-btn-secondary">Cancel</button>
                <button type="button" onClick={confirmAdd} className="btn-warning vi-btn">Add</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Stage 92 (Apply Damage Modal Foundation): faithful migration of the legacy
          showApplyDamageModal (battletracker.html 6907-6973), built on the same
          modal framework as the weapon picker above (weapon-modal-overlay >
          vi-modal-overlay > vi-modal-box). UI FOUNDATION ONLY - no resistance
          calculation (Stage 93), no HP modification / Apply runtime (Stage 94), no
          Damage-button bridge (Stage 95). The modal is not opened by anything this
          stage; setApplyDamageOpen is the seam the future bridge will call.
          Damage-type list reuses the canonical DAMAGE_TYPES (damage/damageTypes.js);
          note the legacy Apply Damage modal's inline list (6936-6948) additionally
          had "Psychic" (13 entries), but the project's canonical source is the
          12-entry damageTypes.js adopted in Stage 76, which is reused here per spec
          rather than re-hardcoding the legacy 13. */}
      {applyDamageOpen ? (
        <div className="apply-damage-modal-overlay">
          <div className="vi-modal-overlay">
            <div className="vi-modal-box">
              <h3 className="vi-modal-title">{'\ud83d\udca5 Apply Damage'}</h3>
              <p className="vi-text-readable">
                <strong>{applyDamageContext.attackerName}</strong>
                {' dealt '}
                <strong className="vi-text-gold">{applyDamageContext.damage}</strong>
                {' damage with ' + applyDamageContext.weaponName}
              </p>

              <div className="vi-modal-field">
                <label className="vi-label-sm">Select Target:</label>
                <select
                  id="damageTarget"
                  className="vi-modal-select-input"
                  value={applyTarget}
                  onChange={function(e) { setApplyTarget(e.target.value); }}
                >
                  {(function() {
                    var options = [];
                    var limit = state.activeMonsterCount;
                    if (limit === undefined || limit > state.monsters.length) { limit = state.monsters.length; }
                    for (var i = 0; i < limit; i++) {
                      var t = state.monsters[i];
                      if (!t) { continue; }
                      var targetName = t.customName ? t.customName : (t.selectedMonster ? t.selectedMonster : ('Monster ' + (i + 1)));
                      var hpText = ' (' + t.currentHP + '/' + t.maxHP + ' HP)';
                      options.push(<option key={i} value={'' + i}>{targetName + hpText}</option>);
                    }
                    return options;
                  })()}
                </select>
              </div>

              <div className="vi-modal-field">
                <label className="vi-label-sm">Damage Type:</label>
                <select
                  id="damageType"
                  className="vi-modal-select-input"
                  value={applyDamageType}
                  onChange={function(e) { setApplyDamageType(e.target.value); }}
                >
                  {DAMAGE_TYPES.map(function(dt) {
                    return <option key={dt} value={dt}>{dt}</option>;
                  })}
                </select>
              </div>

              {/* Resistance info area (legacy resistanceInfo 6953 + calculateResistance
                  6975-7035): Stage 93 wires computeFinalDamage. Shows the status text /
                  colour for immune / resistant / vulnerable; NORMAL hides the area
                  (legacy display:none), so when applyResistanceDisplay is null nothing
                  renders. Engine math owned by damageEngine.computeFinalDamage. */}
              {applyResistanceDisplay ? (
                <div
                  id="resistanceInfo"
                  className="vi-resistance-result vi-modal-field"
                  style={{
                    display: 'block',
                    color: applyResistanceDisplay.color,
                    background: applyResistanceDisplay.background,
                    border: '2px solid ' + applyResistanceDisplay.color
                  }}
                >
                  {applyResistanceDisplay.text}
                </div>
              ) : null}

              <div className="vi-modal-field">
                <label className="vi-label-sm">Final Damage:</label>
                <input
                  type="number"
                  id="adjustedDamage"
                  min="0"
                  className="vi-modal-large-input"
                  value={applyFinalDamage}
                  onChange={function(e) { setApplyFinalDamage(e.target.value); }}
                />
                <div className="vi-hint">Automatically adjusted for resistance/immunity/vulnerability</div>
              </div>

              <div className="vi-modal-actions">
                <button type="button" onClick={closeApplyDamage} className="vi-btn">Cancel</button>
                {/* Stage 94 (Apply Damage Runtime): the Apply button now composes the
                    existing applyDamage runtime via applyDamageFromModal - read target
                    + already-adjusted Final Damage, apply (HP / death / morale owned by
                    applyDamage), close modal. Combat log (legacy addCombatLog 7143) is
                    deferred to Stage 96. */}
                <button type="button" onClick={applyDamageFromModal} className="btn-primary-gold vi-btn">Apply Damage</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default WeaponsView;
